import * as React from "react"
import {
  Modal,
  ModalCard,
  StyledModal,
  StyledModalBody,
  StyledModalActions,
  Button,
  StyledModalHeader,
  useShowSuccessToast,
} from "gatsby-interface"
import { Text } from "./shared/Text"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import {
  ui as uiText,
  gatsbyCloudCdnIntegrationConfig as text,
  errorAlert as errorText,
} from "@modules/locales/default.js"
import { HostingCertificate } from "@modules/graphql/types"

import {
  useRemoveCloudCdnCertificateMutation,
  HostingDetailsForSiteDocument,
  HostingDetailsForSiteQuery,
  HostingDetailsForSiteQueryVariables,
} from "@modules/cdn/gatsbyCloudCdn/queries.generated"

import { FormattedMessage, commonMessageTags } from "@modules/locales"
import { interpolateMessage } from "@modules/locales"
import { stringifyCertDomains } from "../../shared/utils/stringifyCertDomains"

export type RemoveDomainFormProps = {
  siteId: string
  certificate: HostingCertificate
  onDismiss: () => void
  onComplete: () => void
}

export function RemoveCertificateForm({
  siteId,
  certificate,
  onDismiss,
  onComplete,
}: RemoveDomainFormProps) {
  const [
    removeCloudCdnCertificateMutation,
  ] = useRemoveCloudCdnCertificateMutation()
  const showSuccessToast = useShowSuccessToast()
  const [setError, errorAlert] = useTriggerErrorAlert()

  const certDomains = stringifyCertDomains(certificate.domains) ?? ``
  const { managed } = certificate

  const handleRemove = async () => {
    removeCloudCdnCertificateMutation({
      variables: {
        siteId,
        id: certificate.id,
      },
      update: (proxy, { data }) => {
        if (!data?.removeCloudCdnCertificate?.success) {
          return
        }

        const queryInfo = {
          query: HostingDetailsForSiteDocument,
          variables: {
            siteId: siteId,
          },
        }

        const queryData = proxy.readQuery<
          HostingDetailsForSiteQuery,
          HostingDetailsForSiteQueryVariables
        >(queryInfo)

        proxy.writeQuery<
          HostingDetailsForSiteQuery,
          HostingDetailsForSiteQueryVariables
        >({
          ...queryInfo,
          data: {
            ...queryData,
            hostingDetailsForSite: queryData?.hostingDetailsForSite?.map(
              details => {
                details.certificates = details?.certificates?.filter(
                  item => item.id !== certificate.id
                )
                return details
              }
            ),
          },
        })
      },
    })
      .then(({ data }) => {
        const success = data?.removeCloudCdnCertificate?.success ?? false

        if (success) {
          onComplete()
          return showSuccessToast(text.messages.certificateRemoved)
        }

        const message =
          data?.removeCloudCdnCertificate?.message ||
          errorText.messages.somethingWentWrong
        setError({ message })
      })
      .catch(err => {
        setError(err)
      })
  }

  return (
    <Modal
      isOpen={Boolean(certificate)}
      aria-label={
        interpolateMessage<"domains">(
          text.messages.doYouWantToRemoveCertificate,
          { domains: certDomains }
        ) + managed
          ? ` ${text.messages.turnOffAutomaticCertificateWarning}`
          : ``
      }
      onDismiss={onDismiss}
      type="error"
    >
      <ModalCard>
        <StyledModal variant="WARNING">
          <StyledModalHeader onCloseButtonClick={onDismiss}>
            {text.headers.removeCertificate}
          </StyledModalHeader>

          <StyledModalBody>
            <Text>
              <FormattedMessage<"domains", "strong">
                message={text.messages.areYouSureYouWantToRemoveCertificate}
                values={{ domains: certDomains }}
                tags={commonMessageTags}
              />
            </Text>
            {managed && (
              <Text>{text.messages.turnOffAutomaticCertificateWarning}</Text>
            )}

            {errorAlert && (
              <div
                css={theme => ({
                  marginTop: theme.space[5],
                })}
              >
                {errorAlert}
              </div>
            )}

            <StyledModalActions>
              <Button type="button" onClick={onDismiss} variant="SECONDARY">
                {uiText.actions.cancel}
              </Button>
              <Button
                data-testid="remove-cms-integration"
                type="button"
                onClick={handleRemove}
                tone="DANGER"
                variant="PRIMARY"
              >
                {uiText.actions.remove}
              </Button>
            </StyledModalActions>
          </StyledModalBody>
        </StyledModal>
      </ModalCard>
    </Modal>
  )
}
